export type TickGranularity = 'year' | 'month' | 'day';
export type TickHierarchy = 'major' | 'minor';

export interface TimeWheelTick {
  readonly epochMs: number;
  readonly label: string;
  readonly hierarchy: TickHierarchy;
}

const DAY_MS = 24 * 60 * 60 * 1000;

export function chooseTickGranularity(startMs: number, endMs: number): TickGranularity {
  const spanDays = Math.max(1, Math.abs(endMs - startMs) / DAY_MS);
  // Behold manedsnavn gjennom store deler av scrollen. Ar-only blir for grovt i praksis.
  if (spanDays > 3650) return 'year';
  if (spanDays > 120) return 'month';
  return 'day';
}

export function buildTimeWheelTicks(
  startMs: number,
  endMs: number,
  granularity: TickGranularity,
): readonly TimeWheelTick[] {
  const min = Math.min(startMs, endMs);
  const max = Math.max(startMs, endMs);
  if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) return [];
  if (granularity === 'year') return buildYearTicks(min, max);
  if (granularity === 'month') return buildMonthTicks(min, max);
  return buildDayTicks(min, max);
}

function buildYearTicks(min: number, max: number): readonly TimeWheelTick[] {
  const first = startOfYear(min);
  const ticks: TimeWheelTick[] = [];
  for (let cursor = first; cursor <= max; cursor = addYears(cursor, 1)) {
    ticks.push({
      epochMs: cursor,
      label: YEAR_FORMAT.format(new Date(cursor)),
      hierarchy: 'major',
    });
  }
  return ticks;
}

function buildMonthTicks(min: number, max: number): readonly TimeWheelTick[] {
  const first = startOfMonth(min);
  const ticks: TimeWheelTick[] = [];
  let previousYear = -1;
  for (let cursor = first; cursor <= max; cursor = addMonths(cursor, 1)) {
    const date = new Date(cursor);
    const year = date.getUTCFullYear();
    const hierarchy: TickHierarchy = year !== previousYear ? 'major' : 'minor';
    previousYear = year;
    ticks.push({
      epochMs: cursor,
      label: hierarchy === 'major' ? MONTH_YEAR_FORMAT.format(date) : MONTH_FORMAT.format(date),
      hierarchy,
    });
  }
  return ticks;
}

function buildDayTicks(min: number, max: number): readonly TimeWheelTick[] {
  const first = startOfDay(min);
  const ticks: TimeWheelTick[] = [];
  let previousMonth = -1;
  for (let cursor = first; cursor <= max; cursor = addDays(cursor, 1)) {
    const date = new Date(cursor);
    const month = date.getUTCMonth();
    const hierarchy: TickHierarchy = month !== previousMonth ? 'major' : 'minor';
    previousMonth = month;
    ticks.push({
      epochMs: cursor,
      label:
        hierarchy === 'major' ? DAY_MONTH_YEAR_FORMAT.format(date) : DAY_MONTH_FORMAT.format(date),
      hierarchy,
    });
  }
  return ticks;
}

function startOfYear(epochMs: number): number {
  const date = new Date(epochMs);
  return Date.UTC(date.getUTCFullYear(), 0, 1);
}

function startOfMonth(epochMs: number): number {
  const date = new Date(epochMs);
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1);
}

function startOfDay(epochMs: number): number {
  const date = new Date(epochMs);
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

function addYears(epochMs: number, amount: number): number {
  const date = new Date(epochMs);
  return Date.UTC(date.getUTCFullYear() + amount, 0, 1);
}

function addMonths(epochMs: number, amount: number): number {
  const date = new Date(epochMs);
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + amount, 1);
}

function addDays(epochMs: number, amount: number): number {
  return epochMs + amount * DAY_MS;
}

const YEAR_FORMAT = new Intl.DateTimeFormat('nb-NO', {
  year: 'numeric',
  timeZone: 'UTC',
});

const MONTH_YEAR_FORMAT = new Intl.DateTimeFormat('nb-NO', {
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

const MONTH_FORMAT = new Intl.DateTimeFormat('nb-NO', {
  month: 'short',
  timeZone: 'UTC',
});

const DAY_MONTH_FORMAT = new Intl.DateTimeFormat('nb-NO', {
  day: 'numeric',
  month: 'short',
  timeZone: 'UTC',
});

const DAY_MONTH_YEAR_FORMAT = new Intl.DateTimeFormat('nb-NO', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});
